import type { CustomerPreOrder } from "../../../common/jsonRpcTypes";
import DietaryTags from "./DietaryTags";

interface DietarySummaryProps {
  readonly dietaryRequirements: CustomerPreOrder["dietaryRequirements"];
  readonly forNames: string[];
}

/**
 * Summary of the diet types and allergies captured for each guest, shown above
 * the menus.
 *
 * Requirements are stored positionally, one entry per name in `forNames`. When
 * ordering for named guests, entries beyond the end of `forNames` belong to
 * someone who has been removed, so they are not rendered — displaying them
 * would show an anonymous set of allergies that no longer applies to anyone on
 * the booking.
 */
function DietarySummary({ dietaryRequirements, forNames }: DietarySummaryProps) {
  const namedRequirements =
    forNames.length > 0 ? (dietaryRequirements ?? []).slice(0, forNames.length) : (dietaryRequirements ?? []);

  const hasAnyDietary = namedRequirements.some(
    (req) => (req.diet_types?.length ?? 0) > 0 || (req.allergies?.length ?? 0) > 0,
  );

  if (!hasAnyDietary) return null;

  return (
    <div className="gcw:mb-bs-3 gcw:mt-2">
      {namedRequirements.map((req, index) => {
        const hasDietTypes = (req.diet_types?.length ?? 0) > 0;
        const hasAllergies = (req.allergies?.length ?? 0) > 0;

        if (!hasDietTypes && !hasAllergies) return null;

        const personName = forNames[index];

        return (
          <DietaryTags
            key={req.name ?? forNames[index] ?? `person-${index}`}
            dietTypes={req.diet_types}
            allergies={req.allergies}
            personName={forNames.length > 0 && personName ? personName : undefined}
            className="gcw:mb-2"
          />
        );
      })}
    </div>
  );
}

export default DietarySummary;
export type { DietarySummaryProps };
